import { useState } from 'react';
import { Card, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { SkillsView } from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/SkillsView';
import { TabContent } from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/TabContent';
import { type IListItemData } from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/professionalAdapters';
import { CandidatesEmpty } from './CandidatesEmpty';
import { useCandidateSelection } from './useCandidateSelection';

interface IEmbeddedCandidateListProps {
  /** Perm job posting the interview belongs to. */
  postingId: string;
  /** Onsite interview in context — scopes the "Sent" state (invitedInterviewIds). */
  interviewId: string;
  /** Interview date (`YYYY-MM-DD`, office tz) — drives availability/exclusion/order. */
  date?: string;
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Interview Details candidate list (perm). A bordered card holding a flat,
 * tab-less list of eligible candidates with a "Select All" control, reusing the
 * temp invite row (`TabContent`/`EmbeddedListItem`) and skills panel
 * (`SkillsView`). Selection/derivation lives in `useCandidateSelection`.
 */
export const EmbeddedCandidateList = ({
  postingId,
  interviewId,
  date,
  onSelectionChange,
}: IEmbeddedCandidateListProps) => {
  const {
    isLoading,
    rows,
    sentIds,
    selected,
    selectableIds,
    allSelected,
    toggleSelection,
    toggleSelectAll,
  } = useCandidateSelection({ postingId, interviewId, date, onSelectionChange });

  const [skillsPro, setSkillsPro] = useState<IListItemData | null>(null);

  if (skillsPro) {
    return (
      <Card outline elevated>
        <SkillsView
          fullName={skillsPro.fullName}
          profession={skillsPro.profession}
          professionCode={skillsPro.professionCode}
          education={skillsPro.education}
          profileImage={skillsPro.profileImage}
          onBack={() => setSkillsPro(null)}
        />
      </Card>
    );
  }

  return (
    <Card outline data-cy="embedded-candidate-list">
      <Column gap={2} px={3} py={2}>
        <Row align="center" justify="between">
          <Typography variant="subtitle1" bold color={colors.neutral[600]}>
            Invite Candidates
          </Typography>
          {!isLoading && selectableIds.length > 0 && (
            <Typography
              as="span"
              bold
              variant="body1"
              color={colors.secondary[500]}
              className="cursor-pointer"
              data-cy="candidate-select-all"
              role="button"
              tabIndex={0}
              onClick={toggleSelectAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSelectAll();
                }
              }}
            >
              {allSelected ? 'Unselect All' : 'Select All'}
            </Typography>
          )}
        </Row>
        <TabContent
          isLoading={isLoading}
          professionals={rows}
          selectedProfessionals={selected}
          onSelectProfessionals={toggleSelection}
          onViewSkills={setSkillsPro}
          emptyComponent={<CandidatesEmpty />}
          testId="candidate-list-content"
          sentProfessionalIds={sentIds}
        />
      </Column>
    </Card>
  );
};
